import { AnyStyledComponent } from 'styled-components'
import React, { FC, memo, MouseEvent, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { CancellationPolicyTransport } from '../../../../../transports/search-result-listing-static-transport'
import {
    Container,
    GeneralText,
    InnerContainer,
    InnerTextContainer,
    Input,
    Label,
    LabelHandle,
    LabelIcon,
    OuterContainer,
    ReadMoreText,
    Text,
    TextContainer,
} from './cancellation-policy-filter-styles'
import { encodeToBase64 } from '../../../../../utils/encoding-utils'
import { filterGATracking } from '../../utils'
import { FilterHeadPosition, FilterType } from '../../types'
import { FilterItemTransport } from '../../../../../transports/filters-configuration-transport'
import { NewTagContainer, NewTagIcon } from '../header-styles'
import { filterClick, getFilterError, getRegionNodeId, openCancellationPolicyModal } from '../../../../../redux/filters'

import { Dispatch } from '../../../../../redux/types'
import { useRequestUserConfigContext } from '../../../../../hooks/request'

import CrossIcon from '../../../../../assets/icons/filter-icons/cross.svg'
import TickIcon from '../../../../../assets/icons/filter-icons/tick.svg'
import { useTranslation } from '../../../../../hooks/locale'

type Props = {
    filterHeadPosition?: FilterHeadPosition
    filterItems: FilterItemTransport[]
    specificFilterStaticData: CancellationPolicyTransport
}

const CancellationPolicyFilter: FC<Props> = ({ filterHeadPosition, filterItems, specificFilterStaticData }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch<Dispatch>()
    const error = useSelector(getFilterError)
    const regionNodeId = useSelector(getRegionNodeId)
    const user = useRequestUserConfigContext()
    const [isChecked, setIsChecked] = useState<boolean>(filterItems[0].checked)

    const handleItemClick = useCallback(
        (event: MouseEvent<HTMLLabelElement>) => {
            event.preventDefault()
            filterGATracking({
                type: FilterType.CANCELLATION_POLICY,
                name: filterItems[0].title,
                value: filterItems[0].value,
                isChecked: !isChecked,
                regionNodeId,
            })
            setIsChecked(!isChecked)
            const filterItemUrl = new URL(filterItems[0].absoluteLink)
            dispatch(filterClick(encodeToBase64(filterItemUrl.pathname + filterItemUrl.search), false, user))
        },
        [filterItems, isChecked, dispatch, regionNodeId, user],
    )

    const handleReadMoreModal = useCallback(
        (e: MouseEvent<HTMLSpanElement>) => {
            e.preventDefault()
            e.stopPropagation()
            dispatch(openCancellationPolicyModal())
        },
        [dispatch],
    )

    useEffect(() => {
        setIsChecked(filterItems[0].checked)
    }, [filterItems, error])

    return (
        <OuterContainer>
            <InnerContainer>
                <TextContainer>
                    <InnerTextContainer filterHeadPosition={filterHeadPosition}>
                        <Text filterHeadPosition={filterHeadPosition}>{filterItems[0].title}</Text>
                        <NewTagContainer>
                            {t('new')}
                            <NewTagIcon alt="new tag icon" />
                        </NewTagContainer>
                    </InnerTextContainer>
                    <Text>
                        {specificFilterStaticData.description}
                        <ReadMoreText onClick={handleReadMoreModal}>{specificFilterStaticData.readMore}</ReadMoreText>
                    </Text>
                </TextContainer>

                <Container>
                    <Label onClick={handleItemClick} selected={isChecked}>
                        <Input type="checkbox" defaultChecked={isChecked} />
                        <LabelHandle>
                            {isChecked ? (
                                <LabelIcon as={TickIcon as AnyStyledComponent} alt="label icon tick" />
                            ) : (
                                <LabelIcon as={CrossIcon as AnyStyledComponent} alt="label icon cross" />
                            )}
                        </LabelHandle>
                    </Label>
                </Container>
            </InnerContainer>
        </OuterContainer>
    )
}

export default memo(CancellationPolicyFilter)
